import React, { useState, useRef, useEffect } from "react";
import { Search, Check, X, Plus, ChevronDown } from "lucide-react";
import { JOB_TYPES, jobTypeMeta, blankJobItem, enabledJobTypes } from "../lib/jobTypes";

/**
 * Searchable multi-select for job types. Emits an *array of job_item objects*
 * (one per selected type). Selecting a type appends a fresh blank item;
 * unselecting removes ALL items of that type (usually one, but if the user
 * added two "car" items we keep the count in sync).
 *
 * The caller owns the `items` array so it can allow duplicates via a
 * separate "+ Add another" affordance elsewhere; this control only toggles
 * type presence.
 */
export default function JobTypeMultiSelect({ items, onChange, testId = "job-type-multiselect" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectedTypes = new Set((items || []).map((i) => i.job_type));
  // Show only services the shop offers — plus any type an existing item
  // already uses, so a legacy job doesn't disappear just because that
  // type got unticked in Settings.
  const activeTypes = enabledJobTypes();
  const activeSet = new Set(activeTypes.map((t) => t.value));
  const legacyTypesInUse = JOB_TYPES.filter((t) => !activeSet.has(t.value) && selectedTypes.has(t.value));
  const filtered = [...activeTypes, ...legacyTypesInUse].filter((t) =>
    t.label.toLowerCase().includes(query.trim().toLowerCase())
  );

  const toggleType = (t) => {
    if (selectedTypes.has(t.value)) {
      onChange((items || []).filter((i) => i.job_type !== t.value));
    } else {
      onChange([...(items || []), blankJobItem(t.value)]);
    }
  };

  const addDuplicate = (t) => {
    onChange([...(items || []), blankJobItem(t.value)]);
  };

  const removeItem = (id) => {
    onChange((items || []).filter((i) => i.id !== id));
  };

  return (
    <div className="space-y-2" ref={wrapRef} data-testid={testId}>
      <label className="text-label block">Item(s) being worked on</label>

      {/* Selected chips */}
      {(items || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => {
            const meta = jobTypeMeta(it.job_type);
            const Icon = meta.icon;
            return (
              <span
                key={it.id}
                data-testid={`job-type-chip-${it.job_type}`}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-white/10 border border-white/15 text-white text-xs"
              >
                <Icon className="w-3 h-3" />
                {meta.label}
                <button
                  type="button"
                  data-testid={`job-type-remove-${it.id}`}
                  onClick={() => removeItem(it.id)}
                  className="ml-0.5 p-0.5 rounded-full hover:bg-white/20 text-slate-300"
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown trigger */}
      <div className="relative">
        <button
          type="button"
          data-testid="job-type-open"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-300 hover:border-white/25"
        >
          <span className="truncate">
            {items?.length ? `Add another type${items.length > 1 ? " · " + items.length + " selected" : ""}` : "Select job type(s)…"}
          </span>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div
            data-testid="job-type-panel"
            className="absolute z-[60] mt-2 left-0 right-0 rounded-2xl bg-neutral-900 backdrop-blur-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden"
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                autoFocus
                data-testid="job-type-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search job types…"
                className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-slate-500 italic">No matches</div>
              )}
              {filtered.map((t) => {
                const Icon = t.icon;
                const active = selectedTypes.has(t.value);
                return (
                  <div
                    key={t.value}
                    data-testid={`job-type-option-${t.value}`}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${active ? "bg-white/[0.06] text-white" : "text-slate-300 hover:bg-white/[0.04] hover:text-white"}`}
                    onClick={() => toggleType(t)}
                  >
                    <Icon className="w-4 h-4 shrink-0 text-slate-400" />
                    <span className="flex-1">{t.label}</span>
                    {active && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); addDuplicate(t); }}
                          className="p-1 rounded-md bg-white/5 hover:bg-white/15 text-slate-300"
                          title="Add another of this type"
                          data-testid={`job-type-add-more-${t.value}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-white/5 text-[10px] uppercase tracking-widest text-slate-500 flex items-center justify-between">
              <span>Tick to add · click <Plus className="inline w-2.5 h-2.5" /> for a duplicate</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="job-type-done"
                className="text-slate-300 hover:text-white"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
